import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FileText,
  Building2,
  Search,
  RefreshCw,
  Eye,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  Users,
  AlertCircle,
  Filter,
  Archive,
} from 'lucide-react'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import {
  adminApi,
  AdminDocument,
  AdminOrg,
  AdminOrgExchange,
} from '../../services/api/admin'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'
import { useConfirm } from '../../contexts/ConfirmProvider'

export const AdminDocumentsPage: React.FC = () => {
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const [documents, setDocuments] = useState<AdminDocument[]>([])
  const [orgs, setOrgs] = useState<AdminOrg[]>([])
  const [orgExchanges, setOrgExchanges] = useState<AdminOrgExchange[]>([])
  const [loading, setLoading] = useState(true)
  const [orgsLoading, setOrgsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [orgFilter, setOrgFilter] = useState<string>('')
  const [exchangeFilter, setExchangeFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [selectedDocument, setSelectedDocument] = useState<AdminDocument | null>(null)
  const [total, setTotal] = useState(0)

  useEffect(() => {
    if (!currentUser?.isAdmin) {
      navigate('/app')
      return
    }
    loadOrgs()
  }, [currentUser, navigate])

  useEffect(() => {
    if (!orgsLoading) {
      loadDocuments()
    }
  }, [orgsLoading, orgFilter, exchangeFilter, statusFilter])

  useEffect(() => {
    if (orgFilter) {
      loadOrgExchanges(orgFilter)
    } else {
      setOrgExchanges([])
      setExchangeFilter('')
    }
  }, [orgFilter])

  const loadOrgs = async () => {
    try {
      setOrgsLoading(true)
      const data = await adminApi.getOrgs()
      setOrgs(data)
    } catch {
      toast.error('Failed to load organizations')
    } finally {
      setOrgsLoading(false)
    }
  }

  const loadOrgExchanges = async (orgId: string) => {
    try {
      const data = await adminApi.getOrgExchanges(orgId)
      setOrgExchanges(data)
      setExchangeFilter('')
    } catch {
      toast.error('Failed to load exchanges for organization')
    }
  }

  const loadDocuments = async () => {
    try {
      setLoading(true)
      const filters: Record<string, string> = {}
      if (orgFilter) filters['org_id'] = orgFilter
      if (exchangeFilter) filters['exchange_id'] = exchangeFilter
      if (statusFilter) filters['status'] = statusFilter
      const response = await adminApi.getDocuments(
        Object.keys(filters).length > 0 ? filters : undefined
      )
      const allDocuments = response.documents ?? []
      const filtered = searchQuery
        ? allDocuments.filter(
            (d) =>
              (d.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
              (d.documentType ?? '').toLowerCase().includes(searchQuery.toLowerCase())
          )
        : allDocuments
      setDocuments(filtered)
      setTotal(response.total ?? filtered.length)
    } catch {
      toast.error('Failed to load documents')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    loadDocuments()
  }

  const handleDeleteDocument = async (documentId: string) => {
    const ok = await confirm({
      title: 'Delete document?',
      description: 'This action cannot be undone.',
      destructive: true,
      icon: Trash2,
      iconColor: 'rose',
    })
    if (!ok) return
    try {
      await adminApi.deleteDocument(documentId)
      toast.success('Document deleted successfully')
      loadDocuments()
    } catch {
      toast.error('Failed to delete document')
    }
  }

  const handleApproveDocument = async (documentId: string) => {
    try {
      await adminApi.updateDocument(documentId, { status: 'approved' })
      toast.success('Document approved successfully')
      setSelectedDocument(null)
      loadDocuments()
    } catch {
      toast.error('Failed to approve document')
    }
  }

  const handleRejectDocument = async (documentId: string) => {
    try {
      await adminApi.updateDocument(documentId, { status: 'rejected' })
      toast.success('Document rejected')
      setSelectedDocument(null)
      loadDocuments()
    } catch {
      toast.error('Failed to reject document')
    }
  }

  const getStatusBadge = (status: AdminDocument['status']) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </span>
        )
      case 'archived':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
            <Archive className="h-3 w-3 mr-1" />
            Archived
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </span>
        )
    }
  }

  const formatFileSize = (bytes: number | null) => {
    if (!bytes || bytes === 0) return '—'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  if (loading && documents.length === 0 && orgsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <FileText className="h-8 w-8 text-primary" />
            Document Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage documents across all organizations and exchanges
          </p>
        </div>
        <Button
          onClick={loadDocuments}
          variant="outline"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Total Organizations
              </p>
              <p className="text-3xl font-bold text-foreground mt-2">
                {orgs.length}
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Total Documents
              </p>
              <p className="text-3xl font-bold text-foreground mt-2">{total}</p>
            </div>
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <FileText className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Pending Review
              </p>
              <p className="text-3xl font-bold text-foreground mt-2">
                {documents.filter((d) => d.status === 'pending').length}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search documents by name or type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <select
              value={orgFilter}
              onChange={(e) => setOrgFilter(e.target.value)}
              className="px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm"
            >
              <option value="">All Organizations</option>
              {orgs.map((org) => (
                <option
                  key={org.id}
                  value={org.id}
                >
                  {org.name}
                </option>
              ))}
            </select>
            {orgFilter && (
              <select
                value={exchangeFilter}
                onChange={(e) => setExchangeFilter(e.target.value)}
                className="px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm"
              >
                <option value="">All Exchanges</option>
                {orgExchanges.map((exchange) => (
                  <option
                    key={exchange.id}
                    value={exchange.id}
                  >
                    {exchange.name}
                  </option>
                ))}
              </select>
            )}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="archived">Archived</option>
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSearch}
            >
              Search
            </Button>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {loading && (
          <div className="flex items-center justify-center p-8">
            <LoadingSpinner size="md" />
          </div>
        )}
        {!loading && documents.length === 0 && (
          <div className="p-12 text-center">
            <AlertCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              No documents found
            </h2>
            <p className="text-muted-foreground">
              {searchQuery || orgFilter || statusFilter
                ? 'Try adjusting your filters.'
                : 'No documents available.'}
            </p>
          </div>
        )}
        {!loading && documents.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Document
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Uploaded
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {documents.map((document) => (
                  <tr
                    key={document.id}
                    className="hover:bg-secondary/50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 p-2 bg-secondary rounded-lg">
                          <FileText className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-foreground max-w-xs truncate">
                            {document.name ?? '—'}
                          </div>
                          {document.uploadedBy && (
                            <div className="text-xs text-muted-foreground">
                              {document.uploadedBy.firstName} {document.uploadedBy.lastName}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground capitalize">
                      {(document.documentType ?? '').replace(/_/g, ' ') || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatFileSize(document.sizeBytes)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(document.status)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {document.uploadedAt
                        ? new Date(document.uploadedAt).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedDocument(document)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {document.status === 'pending' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleApproveDocument(document.id)}
                              className="text-green-600 hover:text-green-700"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRejectDocument(document.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteDocument(document.id)}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selectedDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-foreground">
                  Document Details
                </h2>
                <Button
                  variant="outline"
                  onClick={() => setSelectedDocument(null)}
                >
                  Close
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    Name
                  </label>
                  <p className="text-slate-900 dark:text-slate-100">{selectedDocument.name ?? '—'}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Type
                    </label>
                    <p className="text-slate-900 dark:text-slate-100 capitalize">
                      {(selectedDocument.documentType ?? '').replace(/_/g, ' ') || '—'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Size
                    </label>
                    <p className="text-foreground">
                      {formatFileSize(selectedDocument.sizeBytes)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Status
                    </label>
                    <div className="mt-1">{getStatusBadge(selectedDocument.status)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      MIME Type
                    </label>
                    <p className="text-foreground">
                      {selectedDocument.mimeType ?? '—'}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    Uploaded
                  </label>
                  <p className="text-slate-900 dark:text-slate-100">
                    {selectedDocument.uploadedAt
                      ? new Date(selectedDocument.uploadedAt).toLocaleString()
                      : '—'}
                  </p>
                </div>
                {selectedDocument.uploadedBy && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Uploaded By
                    </label>
                    <p className="text-foreground">
                      {selectedDocument.uploadedBy.firstName} {selectedDocument.uploadedBy.lastName}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {selectedDocument.uploadedBy.email}
                    </p>
                  </div>
                )}
                {selectedDocument.reviewerNotes && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Reviewer Notes
                    </label>
                    <p className="text-foreground">
                      {selectedDocument.reviewerNotes}
                    </p>
                  </div>
                )}
                {selectedDocument.exchangeId && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Exchange
                    </label>
                    <p className="text-foreground flex items-center gap-1">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      {selectedDocument.exchangeId}
                    </p>
                  </div>
                )}
                <div className="pt-4 border-t border-border flex gap-2">
                  {selectedDocument.status === 'pending' && (
                    <>
                      <Button onClick={() => handleApproveDocument(selectedDocument.id)}>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleRejectDocument(selectedDocument.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleDeleteDocument(selectedDocument.id)
                      setSelectedDocument(null)
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
